import { useMemo } from 'react';

import { fromPromise } from 'xstate5';

import {
  type FetchInitialDataParams,
  type FetchInitialDataResponse,
  useFetchUpsellItems,
} from '@order/features/ordering';
import { useFeatureFlag } from '@order/LaunchDarkly';

import { useIsLoggedIn } from '../../../../context/AuthMachine';
import { getTips } from '../../utils';
import { useBagLoyaltyData } from '../useBagLoyaltyData';
import { useBagPaymentInfo } from '../useBagPaymentInfo';
import { useCart } from '../useCart';
import { useCredits } from '../useCredits';
import { useDefaultPaymentMethod } from '../useDefaultPaymentMethod';
import { useFetchTelemetryData } from '../useFetchTelemetryData';
import { useGiftCards } from '../useGiftCards';
import { usePaymentMethods } from '../usePaymentMethods';

export const useFetchInitialData = () => {
  const isLoggedIn = useIsLoggedIn();
  const dynamicTips = useFeatureFlag('CELS-1191-permanent-dynamic-tip-values');

  const { fetchCart } = useCart();
  const { fetchCredits } = useCredits();
  const { fetchLoyaltyData } = useBagLoyaltyData();
  const { fetchGiftCardBalance } = useGiftCards(fetchCredits);
  const { fetchBagPaymentInfo } = useBagPaymentInfo();
  const { fetchPaymentMethods } = usePaymentMethods();
  const { fetchDefaultPaymentMethod } = useDefaultPaymentMethod();
  const { fetchTelemetryData } = useFetchTelemetryData();
  const { fetchUpsellItems } = useFetchUpsellItems();

  const fetchInitialData = useMemo(
    () =>
      fromPromise<FetchInitialDataResponse, FetchInitialDataParams>(async () => {
        if (!isLoggedIn) {
          const cart = await fetchCart();
          const upsellItems = await fetchUpsellItems(getUpsellParams(cart));

          return {
            cart,
            paymentMethods: [],
            defaultPaymentMethodId: undefined,
            defaultTip: 0,
            availablePoints: 0,
            availableCredit: 0,
            giftCardBalance: 0,
            selectedTip: 0,
            availableTips: [],
            customerId: '',
            firstName: '',
            lastName: '',
            email: '',
            phoneNumber: '',
            stripeCustomerId: '',
            stripeCustomerKey: '',
            stripeSession: '',
            isNewCustomer: true,
            isOptedOutFromLoyalty: false,
            hasDefaultPaymentMethodCard: false,
            upsellItems,
          };
        }

        const [
          cart,
          paymentMethods,
          defaultPaymentMethodId,
          availableCredit,
          giftCardBalance,
          telemetryData,
          loyaltyData,
          bagPaymentInfo,
        ] = await Promise.all([
          fetchCart(),
          fetchPaymentMethods(),
          fetchDefaultPaymentMethod(),
          fetchCredits(),
          fetchGiftCardBalance(),
          fetchTelemetryData(),
          fetchLoyaltyData(),
          fetchBagPaymentInfo(),
        ]);

        const tips = getTips({
          maxTipAmount: cart.maxTipAmount,
          orderChannel: cart.orderChannel,
          dynamicTips,
        });

        const hasDefaultPaymentMethodCard = paymentMethods.some(
          (pm) => pm.id === defaultPaymentMethodId,
        );

        const upsellItems = await fetchUpsellItems(getUpsellParams(cart));

        return {
          cart,
          paymentMethods,
          defaultPaymentMethodId,
          availableCredit,
          giftCardBalance,
          hasDefaultPaymentMethodCard,
          upsellItems,
          ...tips,
          ...bagPaymentInfo,
          ...loyaltyData,
          ...telemetryData,
        };
      }),
    [
      isLoggedIn,
      dynamicTips,
      fetchCart,
      fetchPaymentMethods,
      fetchDefaultPaymentMethod,
      fetchCredits,
      fetchGiftCardBalance,
      fetchTelemetryData,
      fetchLoyaltyData,
      fetchBagPaymentInfo,
      fetchUpsellItems,
    ],
  );

  return { fetchInitialData };
};

// ─── Helpers ───────────────────────────────────────────────────────────────

function getUpsellParams(cart: FetchInitialDataResponse['cart']) {
  return {
    restaurantId: cart.restaurantId ?? '',
    orderChannel: cart.orderChannel,
    productIdsInCart: cart.lineItems.map((lineItem) => lineItem.product?.id ?? ''),
  };
}
